import { useEffect, useState } from "react";
import { Pencil, Trash2, Plus, Send } from "lucide-react";
import { api, formatApiError } from "../../../lib/api";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { ImageField } from "./common";

const EMPTY_BANNER = { title: "", text: "", image: "", cta_label: "", cta_url: "", active: true, starts_at: "", ends_at: "" };

const EmailComposer = () => {
    const [audience, setAudience] = useState({ accounts: 0, subscribers: 0, total_unique: 0, resend_mode: "proxy" });
    const [form, setForm] = useState({ subject: "", body: "", audience: "all" });
    const [sending, setSending] = useState(false);

    const load = () => api.get("/admin/marketing/audience").then((r) => setAudience(r.data)).catch(() => {});
    useEffect(() => {
        load();
    }, []);

    const recipientCount = form.audience === "accounts" ? audience.accounts : form.audience === "subscribers" ? audience.subscribers : audience.total_unique;

    const send = async () => {
        if (!window.confirm(`Send this email to ${recipientCount} recipient(s)? This cannot be undone.`)) return;
        setSending(true);
        try {
            const { data } = await api.post("/admin/marketing/send", form);
            toast.success(`Sent to ${data.sent} recipient(s)${data.failed ? `, ${data.failed} failed (see Emails section)` : ""}`);
            setForm({ subject: "", body: "", audience: "all" });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="marketing-composer">
            <h2 className="font-heading font-extrabold text-lg text-slate-900 mb-1">Send an email to customers</h2>
            <p className="text-xs text-slate-500 mb-4">
                Goes to account holders and newsletter subscribers who opted in — every email includes a working unsubscribe link. Deliveries and failures appear in the Emails section.
            </p>
            {audience.resend_mode !== "direct" ? (
                <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800" data-testid="composer-needs-resend">
                    <strong>Action required:</strong> connect your own Resend account (Integrations section) to send marketing emails. The platform sender is reserved for order and account emails.
                </div>
            ) : (
                <div className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <Label>Audience</Label>
                            <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
                                <SelectTrigger data-testid="composer-audience"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Everyone opted in ({audience.total_unique})</SelectItem>
                                    <SelectItem value="accounts">Account holders ({audience.accounts})</SelectItem>
                                    <SelectItem value="subscribers">Newsletter subscribers ({audience.subscribers})</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="composer-subject">Subject</Label>
                            <Input id="composer-subject" value={form.subject} placeholder="e.g. Fresh stock in this weekend!"
                                onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="composer-subject" />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="composer-body">Message</Label>
                        <Textarea id="composer-body" rows={6} value={form.body}
                            placeholder="Write your message. Blank lines make new paragraphs. Links must start with https://"
                            onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="composer-body" />
                    </div>
                    <div className="flex items-center gap-4">
                        <Button onClick={send} disabled={sending || !form.subject.trim() || !form.body.trim() || recipientCount === 0}
                            className="rounded-full bg-[#0033A0] hover:bg-[#002277]" data-testid="composer-send">
                            <Send className="h-4 w-4 mr-2" />
                            {sending ? "Sending…" : `Send to ${recipientCount} recipient${recipientCount === 1 ? "" : "s"}`}
                        </Button>
                        {recipientCount === 0 && <p className="text-xs text-slate-400">No opted-in recipients yet.</p>}
                    </div>
                </div>
            )}
        </div>
    );
};

export default function MarketingAdmin() {
    const [banners, setBanners] = useState([]);
    const [subs, setSubs] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_BANNER);
    const [saving, setSaving] = useState(false);

    const load = () => {
        api.get("/admin/banners").then((r) => setBanners(r.data)).catch(() => {});
        api.get("/admin/newsletter").then((r) => setSubs(r.data)).catch(() => {});
    };
    useEffect(() => {
        load();
    }, []);

    const activeSubs = subs.filter((s) => !s.unsubscribed_at);

    const openEditor = (b) => {
        setEditing(b || null);
        setForm(
            b
                ? {
                      title: b.title, text: b.text || "", image: b.image || "", cta_label: b.cta_label || "", cta_url: b.cta_url || "",
                      active: b.active !== false,
                      starts_at: b.starts_at ? b.starts_at.slice(0, 16) : "", ends_at: b.ends_at ? b.ends_at.slice(0, 16) : "",
                  }
                : EMPTY_BANNER
        );
        setOpen(true);
    };

    const save = async () => {
        if (!form.title.trim()) return toast.error("Title is required");
        setSaving(true);
        const payload = {
            ...form,
            starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
            ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
        };
        try {
            if (editing) {
                await api.put(`/admin/banners/${editing.id}`, payload);
                toast.success("Banner updated");
            } else {
                await api.post("/admin/banners", payload);
                toast.success("Banner created — it's live on the homepage");
            }
            setOpen(false);
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="admin-marketing" className="space-y-8">
            <EmailComposer />
            <div>
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <h2 className="font-heading font-extrabold text-lg text-slate-900">Hero slideshow photos</h2>
                        <p className="text-xs text-slate-500">Landscape photos in the auto-playing hero at the top of the homepage — add or remove them here. The storefront photo always stays as the first slide, and active discount codes appear as slides automatically.</p>
                    </div>
                    <Button onClick={() => openEditor(null)} data-testid="admin-add-hero-photo" className="rounded-full bg-[#0033A0] hover:bg-[#002277]">
                        <Plus className="h-4 w-4 mr-2" /> Add Hero Photo
                    </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="admin-hero-photos">
                    {banners.filter((b) => b.image).length === 0 && (
                        <p className="col-span-full rounded-2xl border border-dashed border-slate-300 p-6 text-sm text-slate-400" data-testid="hero-photos-empty">
                            No hero photos yet — add your first landscape photo and it will join the slideshow.
                        </p>
                    )}
                    {banners.filter((b) => b.image).map((b) => (
                        <div key={b.id} className="relative rounded-2xl overflow-hidden border border-slate-200 bg-slate-100" data-testid={`hero-photo-${b.id}`}>
                            <img src={b.image} alt={b.title} className="h-36 w-full object-cover" />
                            {!b.active && <span className="absolute top-2 left-2 rounded-full bg-slate-900/80 text-white text-xs font-bold px-2.5 py-0.5">Hidden</span>}
                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent p-3 pt-8 flex items-end justify-between gap-2">
                                <p className="text-white text-sm font-bold truncate">{b.title}</p>
                                <div className="flex gap-1.5 shrink-0">
                                    <button type="button" aria-label={`Edit ${b.title}`} onClick={() => openEditor(b)} data-testid={`edit-hero-photo-${b.id}`}
                                        className="rounded-full bg-white/90 p-1.5 text-[#0033A0] hover:bg-white transition-colors">
                                        <Pencil className="h-3.5 w-3.5" />
                                    </button>
                                    <button type="button" aria-label={`Remove ${b.title}`} data-testid={`delete-hero-photo-${b.id}`}
                                        onClick={() => window.confirm(`Remove "${b.title}" from the hero slideshow?`) && api.delete(`/admin/banners/${b.id}`).then(load).then(() => toast.success("Hero photo removed"))}
                                        className="rounded-full bg-white/90 p-1.5 text-red-600 hover:bg-red-50 transition-colors">
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div>
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <h2 className="font-heading font-extrabold text-lg text-slate-900">Promo banners (no photo)</h2>
                        <p className="text-xs text-slate-500">Text-only announcements shown as cards further down the homepage.</p>
                    </div>
                    <Button onClick={() => openEditor(null)} data-testid="admin-add-banner" variant="outline" className="rounded-full">
                        <Plus className="h-4 w-4 mr-2" /> New Banner
                    </Button>
                </div>
                <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100" data-testid="admin-banners-list">
                    {banners.filter((b) => !b.image).length === 0 && <p className="p-6 text-sm text-slate-400">No text banners yet.</p>}
                    {banners.filter((b) => !b.image).map((b) => (
                        <div key={b.id} className="flex items-center gap-4 p-4" data-testid={`banner-row-${b.id}`}>
                            <div className="flex-1 min-w-0">
                                <p className="font-heading font-bold text-slate-900 flex items-center gap-2">
                                    {b.title}
                                    {!b.active && <span className="rounded-full bg-slate-200 text-slate-600 text-xs px-2 py-0.5">Inactive</span>}
                                </p>
                                <p className="text-xs text-slate-500 truncate">
                                    {b.text}
                                    {b.ends_at ? ` · ends ${new Date(b.ends_at).toLocaleDateString("en-GB")}` : ""}
                                </p>
                            </div>
                            <Button variant="outline" size="sm" className="rounded-full" onClick={() => openEditor(b)} data-testid={`edit-banner-${b.id}`}>
                                <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="sm" className="rounded-full text-red-600 border-red-200 hover:bg-red-50"
                                data-testid={`delete-banner-${b.id}`}
                                onClick={() => window.confirm("Delete this banner?") && api.delete(`/admin/banners/${b.id}`).then(load).then(() => toast.success("Deleted"))}>
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </div>
                    ))}
                </div>
            </div>

            <div>
                <h2 className="font-heading font-extrabold text-lg text-slate-900 mb-1" data-testid="subscribers-heading">
                    Newsletter subscribers ({activeSubs.length} active)
                </h2>
                <p className="text-xs text-slate-500 mb-3">
                    Consent is recorded at signup/checkout. Subscribers can unsubscribe via their unique link.
                </p>
                <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100" data-testid="admin-subscribers-list">
                    {subs.length === 0 && <p className="p-6 text-sm text-slate-400">No subscribers yet.</p>}
                    {subs.map((s) => (
                        <div key={s.id} className="flex items-center gap-4 p-4" data-testid={`subscriber-${s.id}`}>
                            <div className="flex-1 min-w-0">
                                <p className="font-bold text-slate-900 text-sm">{s.email}</p>
                                <p className="text-xs text-slate-500">
                                    via {s.source} · {new Date(s.subscribed_at).toLocaleDateString("en-GB")}
                                </p>
                            </div>
                            {s.unsubscribed_at ? (
                                <span className="rounded-full bg-slate-200 text-slate-600 text-xs font-bold px-3 py-1">Unsubscribed</span>
                            ) : (
                                <span className="rounded-full bg-green-100 text-green-700 text-xs font-bold px-3 py-1">Active</span>
                            )}
                            <Button variant="outline" size="sm" className="rounded-full text-red-600 border-red-200 hover:bg-red-50"
                                data-testid={`delete-subscriber-${s.id}`}
                                onClick={() => window.confirm(`Remove ${s.email}?`) && api.delete(`/admin/newsletter/${s.id}`).then(load).then(() => toast.success("Removed"))}>
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </div>
                    ))}
                </div>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="sm:max-w-lg" data-testid="banner-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">{editing ? "Edit Banner" : "New Homepage Banner"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="space-y-1.5">
                            <Label htmlFor="bf-title">Title *</Label>
                            <Input id="bf-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                                placeholder="e.g. Weekend Special: 10% off Goat Meat" data-testid="bf-title" />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="bf-text">Text</Label>
                            <Input id="bf-text" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })}
                                placeholder="Short supporting line" data-testid="bf-text" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Landscape photo (optional — turns this into a hero carousel slide)</Label>
                            <ImageField value={form.image} onChange={(url) => setForm({ ...form, image: url })} testId="bf-image" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <Label htmlFor="bf-cta-label">Button label</Label>
                                <Input id="bf-cta-label" value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })}
                                    placeholder="Shop now" data-testid="bf-cta-label" />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="bf-cta-url">Button link</Label>
                                <Input id="bf-cta-url" value={form.cta_url} onChange={(e) => setForm({ ...form, cta_url: e.target.value })}
                                    placeholder="/products" data-testid="bf-cta-url" />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="bf-start">Starts</Label>
                                <Input id="bf-start" type="datetime-local" value={form.starts_at}
                                    onChange={(e) => setForm({ ...form, starts_at: e.target.value })} data-testid="bf-starts" />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="bf-end">Ends</Label>
                                <Input id="bf-end" type="datetime-local" value={form.ends_at}
                                    onChange={(e) => setForm({ ...form, ends_at: e.target.value })} data-testid="bf-ends" />
                            </div>
                        </div>
                        <label className="flex items-center gap-2 text-sm font-bold text-slate-700 cursor-pointer">
                            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })}
                                className="accent-[#0033A0]" data-testid="bf-active" />
                            Active (visible on homepage)
                        </label>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
                        <Button onClick={save} disabled={saving} className="rounded-full bg-[#0033A0] hover:bg-[#002277]" data-testid="bf-save">
                            {saving ? "Saving…" : "Save Banner"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
